import React from "react";
import type { CompanyDetails } from "@services/bayHireApi";
import { useTranslation } from "react-i18next";

interface FieldProps {
  label: string;
  value: React.ReactNode;
  testId?: string;
}

// Megaplan (2026-08-05): label and value used to stack vertically (label on its own line, value
// directly below it) - changed to render inline, matching the "label: value" convention this same
// detail page already uses a few sections below for the Receipt card's own metadata lines
// (receiptNumber/receiptAmount/receiptDate in BayHireRequestDetail.tsx) - a single text flow with
// a real space after the colon, not two separately-boxed elements pinned apart by a flex `gap`
// (which still read as cramped no matter how large the gap, since the label's own trailing edge
// had no breathing room built in).
const Field: React.FC<FieldProps> = ({ label, value, testId }) => (
  <div className="py-3">
    <p className="text-sm text-gray-900 break-words" data-testid={testId}>
      <span className="text-sm font-medium text-gray-500">{label}: </span>
      {value}
    </p>
  </div>
);

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 border-b border-gray-100 last:border-b-0">
    {children}
  </div>
);

interface CompanyDetailsCardProps {
  company: CompanyDetails;
  /**
   * DFT-0044 FE-41: the baseline puts the responsible person and their designation in this card,
   * not in `Bay Information`. Optional so the component keeps rendering if a caller has no request
   * context (its doc comment describes a create-form usage; today only the detail page renders it,
   * but an optional prop keeps that claim true rather than silently breaking it).
   */
  responsiblePersonName?: string | null;
  responsiblePersonDesignation?: string | null;
}

/**
 * DFT-0027: Figma p10 "Company details" card - the Port Operator company behind the
 * request, rendered with the same bordered-section + two-column label/value grid as
 * BayInformationCard, and placed above it per the design.
 *
 * <p>Shared between the request detail page (`company` resolved from an existing request's
 * companyId) and the create form (`company` resolved from the caller's own token, before a
 * request exists) - both are the identical `CompanyDetails` shape from the identical BFF schema,
 * so one component renders both rather than two near-duplicates.
 *
 * <p>DFT-0044 (Q13, answered 2026-07-29): `FAX` is REINSTATED, reversing the descoping this card
 * previously implemented ("product confirmed it is out of scope"). It renders `-` today, matching
 * every other missing-value field, because `reference.company` genuinely has no fax column - Q13-a
 * owns finding a source. This is a recorded decision, not an omission to "fix" by removing the row.
 */
const CompanyDetailsCard: React.FC<CompanyDetailsCardProps> = ({
  company,
  responsiblePersonName,
  responsiblePersonDesignation,
}) => {
  const { t } = useTranslation();
  const valueOrDash = (value?: string | null) => value || "-";

  return (
    <section
      className="border border-gray-200 rounded-md bg-white p-6 mb-6"
      data-testid="bay-hire-detail-company-details"
    >
      <h3 className="text-base font-semibold text-gray-900 mb-2">
        {t("bay-hire.detail.company-details")}
      </h3>

      <Row>
        <Field
          label={t("bay-hire.detail.portOperatorCompanyName")}
          testId="bay-hire-detail-company-name"
          value={valueOrDash(company.companyNameEnglish)}
        />
        <Field
          label={t("bay-hire.detail.telephoneNo")}
          testId="bay-hire-detail-company-telephone"
          value={valueOrDash(company.proOfficeNumber)}
        />
      </Row>

      <Row>
        <Field
          label={t("bay-hire.detail.poBox")}
          testId="bay-hire-detail-company-po-box"
          value={valueOrDash(company.poBox)}
        />
        <Field
          label={t("bay-hire.detail.emailAddress")}
          testId="bay-hire-detail-company-email"
          value={valueOrDash(company.proEmail)}
        />
      </Row>

      <Row>
        <Field
          label={t("bay-hire.detail.emirate")}
          testId="bay-hire-detail-company-emirate"
          value={valueOrDash(company.emirates)}
        />
        <Field
          label={t("bay-hire.detail.fax")}
          testId="bay-hire-detail-company-fax"
          value={valueOrDash(company.fax)}
        />
      </Row>

      {/* DFT-0044 FE-41: moved here from `Bay Information`, where DFT-0027 had placed them - the
          baseline groups the responsible person with the company, not with the bays. */}
      <Row>
        <Field
          label={t("bay-hire.detail.responsibleCompanyPerson")}
          testId="bay-hire-detail-company-responsible-person"
          value={valueOrDash(responsiblePersonName)}
        />
        <Field
          label={t("bay-hire.detail.responsibleDesignation")}
          testId="bay-hire-detail-company-responsible-designation"
          value={valueOrDash(responsiblePersonDesignation)}
        />
      </Row>
    </section>
  );
};

export default CompanyDetailsCard;
